import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import {
  EntityAction,
  EntityActionHandlerType,
  EntityActionType
} from '../interfaces/entity-actions';
import type { EntityActionsProps } from '../EntityActions';
import { useConfirmDialogForEntityActions } from './useConfirmDialogForEntityActions';
import { useAnalyticsTracker } from '../../tracking/hooks/useAnalyticsTracker';

export function useEntityActions<A extends string | number | symbol, E>({
  actions,
  getEntityFromActionResponse,
  updateEntityOnSuccess,
  entity,
  confirmationDialogProps
}: EntityActionsProps<A, E>) {
  const navigate = useNavigate();
  const { eventTrack } = useAnalyticsTracker();

  const { inlineActions, menuActions } = useMemo(() => {
    return actions.reduce(
      (acc, action) => {
        if (
          [
            EntityActionType.INLINE,
            EntityActionType.INLINE_LINK,
            EntityActionType.INLINE_ICON
          ].includes(action.type)
        ) {
          acc.inlineActions.push(action);
        } else if (action.type === EntityActionType.MENU) {
          acc.menuActions.push(action);
        }

        return acc;
      },
      { inlineActions: [] as Array<EntityAction<A>>, menuActions: [] as Array<EntityAction<A>> }
    );
  }, [actions]);

  const { showConfirmDialog } = useConfirmDialogForEntityActions<A, E>({
    entity,
    updateEntity: updateEntityOnSuccess,
    getEntityFromActionResponse,
    confirmationDialogProps
  });

  const onActionClick = (action: EntityAction<A>) => {
    if (action.trackingProps) {
      eventTrack({
        ...action.trackingProps
      });
    }

    if (action.actionHandlerType === EntityActionHandlerType.NAVIGATE && action.routeOptions) {
      navigate(action.routeOptions.route, action.routeOptions.options);
      return;
    }

    if (action.actionHandlerType === EntityActionHandlerType.CONFIRM) {
      showConfirmDialog(action.action);
      return;
    }

    if (
      action.actionHandlerType === EntityActionHandlerType.CUSTOM &&
      typeof action.onClick === 'function'
    ) {
      action.onClick();
    }
  };

  return {
    inlineActions,
    menuActions,
    onActionClick
  };
}
